function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}
/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { OuiFormFieldset } from '../form_fieldset';
import { OuiCheckbox } from './checkbox';

// Must omit inherit `onChange` properties or else TS complains when applying to the OuiRadio

export var OuiCheckboxGroup = function OuiCheckboxGroup(_ref) {
  var _ref$options = _ref.options,
    options = _ref$options === void 0 ? [] : _ref$options,
    _ref$idToSelectedMap = _ref.idToSelectedMap,
    idToSelectedMap = _ref$idToSelectedMap === void 0 ? {} : _ref$idToSelectedMap,
    onChange = _ref.onChange,
    className = _ref.className,
    disabled = _ref.disabled,
    compressed = _ref.compressed,
    legend = _ref.legend,
    rest = _objectWithoutProperties(_ref, ["options", "idToSelectedMap", "onChange", "className", "disabled", "compressed", "legend"]);
  var checkboxes = options.map(function (option, index) {
    var isOptionDisabled = option.disabled,
      optionClass = option.className,
      optionRest = _objectWithoutProperties(option, ["disabled", "className"]);
    return /*#__PURE__*/React.createElement(OuiCheckbox, _extends({
      className: classNames('ouiCheckboxGroup__item', optionClass),
      key: index,
      checked: idToSelectedMap[option.id],
      disabled: disabled || isOptionDisabled,
      onChange: onChange.bind(null, option.id),
      compressed: compressed
    }, optionRest));
  });
  if (!!legend) {
    // Be sure to pass down the compressed option to the legend
    legend.compressed = compressed;
    return /*#__PURE__*/React.createElement(OuiFormFieldset, _extends({
      className: className,
      legend: legend
    }, rest), checkboxes);
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    className: className
  }, rest), checkboxes);
};

// @internal
OuiCheckboxGroup.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired
  }).isRequired).isRequired,
  idToSelectedMap: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  /**
     * Tightens up the spacing between checkbox rows and sends down the
     * compressed prop to the checkbox itself
     */
  compressed: PropTypes.bool,
  disabled: PropTypes.bool,
  /**
     * If the individual labels for each radio do not provide a sufficient description, add a legend.
     * Wraps the group in a `OuiFormFieldset` which adds an `OuiLegend` for titling the whole group.
     * Accepts an `OuiFormLegendProps` shape.
     */
  legend: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    /**
         * ReactNode to render as this component's content
         */
    children: PropTypes.node.isRequired,
    /**
         * For a hidden legend that is still visible to the screen reader, set to 'hidden'
         */
    display: PropTypes.oneOf(["hidden", "visible"]),
    compressed: PropTypes.bool
  })
};
// @internal
export var OuiCompressedCheckboxGroup = props => <OuiCheckboxGroup {...props} compressed />;
OuiCompressedCheckboxGroup.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired
  }).isRequired).isRequired,
  idToSelectedMap: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /**
     * If the individual labels for each radio do not provide a sufficient description, add a legend.
     * Wraps the group in a `OuiFormFieldset` which adds an `OuiLegend` for titling the whole group.
     * Accepts an `OuiFormLegendProps` shape.
     */
  legend: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    children: PropTypes.node.isRequired,
    display: PropTypes.oneOf(["hidden", "visible"]),
    compressed: PropTypes.bool
  })
};
try {
  OuiCheckboxGroup.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src2210237963/src/src/components/form/checkbox/checkbox_group.tsx',
    description: '',
    displayName: 'OuiCheckboxGroup',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      options: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'options',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiCheckboxGroupOption[]'
        }
      },
      idToSelectedMap: {
        defaultValue: {
          value: '{}'
        },
        description: '',
        name: 'idToSelectedMap',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiCheckboxGroupIdToSelectedMap'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(optionId: string) => void'
        }
      },
      compressed: {
        defaultValue: null,
        description: 'Tightens up the spacing between checkbox rows and sends down the\n' + 'compressed prop to the checkbox itself',
        name: 'compressed',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      legend: {
        defaultValue: null,
        description: 'Adds an OuiFormLegend element as the first child\n' + 'If the individual labels for each radio do not provide a sufficient description, add a legend.\n' + 'Wraps the group in a `OuiFormFieldset` which adds an `OuiLegend` for titling the whole group.\n' + 'Accepts an `OuiFormLegendProps` shape.',
        name: 'legend',
        parent: {
          fileName: 'src/src/components/form/form_fieldset/form_fieldset.tsx',
          name: 'OuiFormFieldsetProps'
        },
        declarations: [{
          fileName: 'src/src/components/form/form_fieldset/form_fieldset.tsx',
          name: 'OuiFormFieldsetProps'
        }, {
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLLegendElement> & CommonProps & { children: ReactNode; display?: "hidden" | "visible"; compressed?: boolean; }'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'OuiFormFieldsetProps', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}
try {
  OuiCompressedCheckboxGroup.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src2210237963/src/src/components/form/checkbox/checkbox_group.tsx',
    description: '',
    displayName: 'OuiCompressedCheckboxGroup',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      options: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'options',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiCheckboxGroupOption[]'
        }
      },
      idToSelectedMap: {
        defaultValue: {
          value: '{}'
        },
        description: '',
        name: 'idToSelectedMap',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiCheckboxGroupIdToSelectedMap'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(optionId: string) => void'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      legend: {
        defaultValue: null,
        description: 'Adds an OuiFormLegend element as the first child\n' + 'If the individual labels for each radio do not provide a sufficient description, add a legend.\n' + 'Wraps the group in a `OuiFormFieldset` which adds an `OuiLegend` for titling the whole group.\n' + 'Accepts an `OuiFormLegendProps` shape.',
        name: 'legend',
        parent: {
          fileName: 'src/src/components/form/form_fieldset/form_fieldset.tsx',
          name: 'OuiFormFieldsetProps'
        },
        declarations: [{
          fileName: 'src/src/components/form/form_fieldset/form_fieldset.tsx',
          name: 'OuiFormFieldsetProps'
        }, {
          fileName: 'src/src/components/form/checkbox/checkbox_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLLegendElement> & CommonProps & { children: ReactNode; display?: "hidden" | "visible"; compressed?: boolean; }'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'OuiFormFieldsetProps', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}